import { TransportMode, ModeFares } from '../types/fare';

/**
 * Client-side fare estimation for alternative routes
 * This matches the backend fare calculation logic for consistency
 */

/**
 * Calculate fare for a given distance and mode
 */
export function calculateFareForDistance(
  distanceKm: number,
  mode: TransportMode,
  weatherMultiplier: number = 0,
  hotspotMultiplier: number = 0
): ModeFares {
  let basePerKm: number;
  let minMultiplier: number;
  let maxMultiplier: number;

  if (mode === 'tuk') {
    // Tuk-tuk rates
    if (distanceKm < 5) {
      basePerKm = 100;
      minMultiplier = 0.8;
      maxMultiplier = 1.2;
    } else if (distanceKm < 15) {
      basePerKm = 90;
      minMultiplier = 0.8;
      maxMultiplier = 1.15;
    } else if (distanceKm < 50) {
      basePerKm = 85;
      minMultiplier = 0.85;
      maxMultiplier = 1.15;
    } else {
      basePerKm = 80;
      minMultiplier = 0.9;
      maxMultiplier = 1.1;
    }
  } else {
    // Taxi rates
    if (distanceKm < 5) {
      basePerKm = 150;
      minMultiplier = 0.85;
      maxMultiplier = 1.15;
    } else if (distanceKm < 15) {
      basePerKm = 130;
      minMultiplier = 0.85;
      maxMultiplier = 1.1;
    } else if (distanceKm < 50) {
      basePerKm = 110;
      minMultiplier = 0.9;
      maxMultiplier = 1.1;
    } else {
      basePerKm = 95;
      minMultiplier = 0.9;
      maxMultiplier = 1.05;
    }
  }

  const base = basePerKm * distanceKm;
  const min = base * minMultiplier;
  const max = base * maxMultiplier;

  // Round to nearest 50
  return {
    min: roundTo50(min),
    max: roundTo50(max),
    base: roundTo50(base)
  };
}

/**
 * Calculate ceiling with weather and hotspot multipliers
 */
export function calculateCeiling(
  maxFare: number,
  weatherMultiplier: number = 0,
  hotspotMultiplier: number = 0
): number {
  const totalMultiplier = 1 + weatherMultiplier + hotspotMultiplier;
  return roundTo50(maxFare * totalMultiplier);
}

/**
 * Round to nearest 50
 */
export function roundTo50(value: number): number {
  return Math.round(value / 50) * 50;
}

/**
 * Get recommended mode based on distance
 */
export function getRecommendedMode(distanceKm: number): TransportMode | 'both' {
  if (distanceKm < 15) {
    return 'tuk';
  } else if (distanceKm >= 15 && distanceKm <= 50) {
    return 'both';
  } else {
    return 'taxi';
  }
}

/**
 * Calculate fare range as percentage
 * Used for visual price bar positioning
 */
export function calculateFarePercentage(
  fare: number,
  min: number,
  ceiling: number
): number {
  if (ceiling <= min) return 0;
  const percentage = ((fare - min) / (ceiling - min)) * 100;
  return Math.max(0, Math.min(100, percentage));
}

/**
 * Get fare zone color based on fare position
 */
export function getFareZoneColor(percentage: number): string {
  if (percentage < 40) {
    return '#10b981'; // Green - Fair
  } else if (percentage < 70) {
    return '#3b82f6'; // Blue - Ideal
  } else if (percentage < 90) {
    return '#f59e0b'; // Yellow - High
  } else {
    return '#ef4444'; // Red - Overpriced
  }
}

/**
 * Get fare zone label
 */
export function getFareZoneLabel(percentage: number): string {
  if (percentage < 40) {
    return 'Fair Price';
  } else if (percentage < 70) {
    return 'Good Deal';
  } else if (percentage < 90) {
    return 'High';
  } else {
    return 'Overpriced';
  }
}

/**
 * Format time duration from minutes
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (mins === 0) {
    return `${hours} hr`;
  }

  return `${hours} hr ${mins} min`;
}

/**
 * Format distance with unit
 */
export function formatDistance(km: number): string {
  return `${km.toFixed(1)} km`;
}

/**
 * Calculate savings text
 */
export function formatSavings(
  distanceKm: number,
  timeMins: number
): string {
  const parts: string[] = [];

  if (Math.abs(distanceKm) >= 0.5) {
    const sign = distanceKm > 0 ? '+' : '';
    parts.push(`${sign}${distanceKm.toFixed(1)} km`);
  }

  if (Math.abs(timeMins) >= 1) {
    const sign = timeMins > 0 ? '+' : '';
    parts.push(`${sign}${timeMins} min`);
  }

  return parts.length > 0 ? parts.join(', ') : 'Same';
}

/**
 * Get transport mode display name
 */
export function getModeName(mode: TransportMode): string {
  return mode === 'tuk' ? 'Tuk-Tuk' : 'Taxi';
}

/**
 * Get transport mode emoji
 */
export function getModeEmoji(mode: TransportMode): string {
  return mode === 'tuk' ? '🛺' : '🚖';
}

